"use client";

import { useActionState, useState } from "react";
import { deleteAccountAction, type ActionState } from "@frontend/lib/actions";
import { SettingsCard } from "@frontend/components/settings/section";
import { Button, Callout, Field, LinkButton } from "@frontend/components/ui";
import { useViewer } from "@frontend/lib/viewer";

/**
 * Self-serve permanent account deletion. Used on Settings → Security and on
 * /delete-account when the visitor is signed in.
 */
export function DeleteAccountPanel({ compact }: { compact?: boolean }) {
  const { viewer, loading } = useViewer();
  const [open, setOpen] = useState(false);
  const [state, formAction, pending] = useActionState<ActionState, FormData>(
    deleteAccountAction,
    {},
  );

  if (loading) return null;

  if (!viewer || viewer.isAdmin) {
    if (compact) return null;
    return (
      <SettingsCard
        title="Delete account"
        description="Permanently close your SendCollab account and remove personal data we can delete."
      >
        <p className="text-sm text-ink-muted">
          Sign in to delete your account yourself. If you cannot sign in, see the steps below for
          contacting support.
        </p>
        <div className="mt-4">
          <LinkButton href="/login?next=/delete-account" variant="secondary" size="sm">
            Sign in to delete
          </LinkButton>
        </div>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      title="Delete account"
      description="Permanently close this account. Your phone number and public username become available again. Collaboration records the other party still needs may be kept in anonymized form."
    >
      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-md text-sm text-ink-muted">
            This cannot be undone. You will be signed out immediately.
          </p>
          <Button type="button" variant="danger" size="sm" onClick={() => setOpen(true)}>
            Delete account…
          </Button>
        </div>
      ) : (
        <form action={formAction} className="space-y-4">
          <Callout tone="amber" title="This is permanent">
            Profile, payout details, sessions, and push tokens are removed. Open collabs stay
            available to the other party as “Deleted account” where needed.
          </Callout>
          <Field label='Type DELETE to confirm' hint="Exact spelling. Caps are fine either way.">
              <input
                name="confirmation"
                autoComplete="off"
                spellCheck={false}
                placeholder="DELETE"
                className="field-input"
                disabled={pending}
                required
              />
            </Field>
          {state.error ? <Callout tone="red">{state.error}</Callout> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="danger" size="sm" disabled={pending}>
              {pending ? "Deleting…" : "Permanently delete account"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pending}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </SettingsCard>
  );
}
